import { Col, Row, Space, Table, Tag, Tooltip } from "antd";
import { ActionStatus } from "common/enums";
import { FullHeightContainerLayout, ProhibitedArea } from "components";
import moment from "moment";
import React from "react";
import { Icon } from "semantic-ui-react";

const { Column } = Table;

interface DataSyndicationSubscriptionListProps {
    items: IDataSyndicationSubscription[];
    onViewSubscription: (subscriptionId: string) => void;
    onEditSubscription: (subscriptionId: string) => void;
    permission?: IPermissionState;
    confirmDataSyndicationSubscriptionActions?: Record<string, { status: string; error?: IActionError }>;
}

const DataSyndicationSubscriptionList: React.FC<DataSyndicationSubscriptionListProps> = ({
    items,
    onViewSubscription,
    onEditSubscription,
    permission = { canEdit: true, canView: true, canDelete: true, canAdd: true },
    confirmDataSyndicationSubscriptionActions = {},
}) => {
    if (!permission) {
        return <ProhibitedArea />;
    }

    return (
        <Row style={{ flexDirection: "column", flexGrow: 1, height: "100%" }}>
            <Col span={24} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <FullHeightContainerLayout
                    showHeader={false}
                    showFooter={true}
                    content={
                        <Table size="small" rowKey="subscriptionId" dataSource={items} pagination={false} scroll={{ y: "100vh" }} className="container-height-100">
                            <Column
                                width="8em"
                                render={(_, subscription: IDataSyndicationSubscription) => (
                                    <Space wrap>
                                        <Tooltip title="View subscription">
                                            <Icon
                                                disabled={!permission.canView}
                                                color="blue"
                                                name="eye"
                                                className="action-cursor"
                                                onClick={() => onViewSubscription(subscription.subscriptionId)}
                                            />
                                        </Tooltip>
                                        {subscription.status === "PENDING" && (
                                            <Tooltip title="Confirm subscription">
                                                <Icon
                                                    disabled={!permission.canEdit}
                                                    color="blue"
                                                    name="pencil"
                                                    className="action-cursor"
                                                    onClick={() => onEditSubscription(subscription.subscriptionId)}
                                                />
                                            </Tooltip>
                                        )}
                                    </Space>
                                )}
                            />
                            <Column
                                width="15%"
                                dataIndex="name"
                                title="Name"
                                ellipsis={{
                                    showTitle: false,
                                }}
                                render={(value: string) => (
                                    <Tooltip placement="topLeft" title={value}>
                                        {value}
                                    </Tooltip>
                                )}
                            />
                            <Column
                                width="15%"
                                dataIndex="description"
                                title="Purpose"
                                ellipsis={{
                                    showTitle: false,
                                }}
                                render={(value: string) => (
                                    <Tooltip placement="topLeft" title={value}>
                                        {value}
                                    </Tooltip>
                                )}
                            />
                            <Column
                                width="10%"
                                dataIndex="status"
                                title="Status"
                                ellipsis={{
                                    showTitle: false,
                                }}
                                render={(status: string, subscription: IDataSyndicationSubscription) => {
                                    const confirmAction = confirmDataSyndicationSubscriptionActions[subscription.subscriptionId];
                                    if (confirmAction?.status === ActionStatus.SUCCESS) {
                                        return <Tag color="green">CONFIRMED</Tag>;
                                    }
                                    return <Tag color={status === "PENDING" ? "orange" : "green"}>{status}</Tag>;
                                }}
                            />
                            <Column
                                dataIndex="validFrom"
                                title="Valid From"
                                ellipsis={{
                                    showTitle: false,
                                }}
                                render={(value: string) =>
                                    value && (
                                        <Tooltip placement="topLeft" title={value}>
                                            {moment.utc(value).toDate().toLocaleString()}
                                        </Tooltip>
                                    )
                                }
                            />
                            <Column
                                dataIndex="validUntil"
                                title="Valid Until"
                                ellipsis={{
                                    showTitle: false,
                                }}
                                render={(value: string) =>
                                    value && (
                                        <Tooltip placement="topLeft" title={value}>
                                            {moment.utc(value).toDate().toLocaleString()}
                                        </Tooltip>
                                    )
                                }
                            />
                            <Column
                                dataIndex="invocationDate"
                                title="Last Invoked Date"
                                ellipsis={{
                                    showTitle: false,
                                }}
                                render={(value: string) =>
                                    value && (
                                        <Tooltip placement="topLeft" title={value}>
                                            {moment.utc(value).toDate().toLocaleString()}
                                        </Tooltip>
                                    )
                                }
                            />
                            <Column
                                dataIndex="createdDate"
                                title="Created Date"
                                ellipsis={{
                                    showTitle: false,
                                }}
                                render={(value: string) => (
                                    <Tooltip placement="topLeft" title={value}>
                                        {moment.utc(value).toDate().toLocaleString()}
                                    </Tooltip>
                                )}
                            />
                        </Table>
                    }
                />
            </Col>
        </Row>
    );
};

export default DataSyndicationSubscriptionList;
